import { Autocomplete, CircularProgress, Paper, Popper, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeUnit, EAttributeUnitFamily } from '../../@types/AttributeTypes';
import { EUnitType } from '../../@types/Enums';
import { IAttribute } from '../../models/standard/AttributeModel';
import { useLoginContext } from '../../provider/LoginProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { AttributeSelectionInput } from '../../utils/attributes/attributeCreationUtils';
import {
  attributeUnitDisplayMap, getLocalizedAttributeLabel, IMPERIAL_TO_METRIC, METRIC_TO_IMPERIAL, unitFamilyToUnitRecord
} from '../../utils/attributes/attributeUtils';
import { createLocalizedLabelComparator } from '../../utils/localizedSortUtils';

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    margin: 'auto',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(4),
    '& .MuiInputBase-root': {
      borderRadius: '16px',
      fontSize: '1rem',
      padding: theme.spacing(1, 2),
      border: '1.5px solid #d3d3d3',
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiInputBase-root': { borderColor: '#b3b3b3' },
    '& .Mui-focused .MuiInputBase-root': { borderColor: '#b3b3b3' },
    '& .MuiAutocomplete-endAdornment': {
      alignItems: 'center',
      display: 'flex',
      right: '14px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '1rem',
      color: '#464646',
      top: '50%',
      transform: 'translate(14px, -50%) scale(1)',
      '&.Mui-focused': {
        color: '#464646',
      },
    },
    '& .MuiInputLabel-shrink': {
      top: 0,
      transform: 'translate(14px, -9px) scale(0.75)',
      backgroundColor: '#fff',
      padding: '0 4px',
      color: '#464646',
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(1.5, 2),
    },
  },
  popper: {
    marginTop: '0.1rem !important',
    zIndex: 1500,
    overflow: 'visible',
    '& .MuiAutocomplete-listbox': {
      borderRadius: '16px !important',
      overflow: 'auto',
      paddingRight: '3px',
      backgroundColor: '#fff',
    },
    borderRadius: '16px',
  },
  paper: {
    borderRadius: '16px',
    boxShadow: '0px 8px 16px rgba(0,0,0,0.15)',
    border: '1px solid #d3d3d3',
    borderTop: 'none',
    marginTop: '-1px',
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  listbox: {
    maxHeight: '260px',
    padding: '0 6px 0 0',
    borderRadius: '16px !important',
    overflow: 'auto',
    backgroundColor: '#fff',
    '& .MuiAutocomplete-option': {
      fontSize: '1rem',
      padding: theme.spacing(2, 2.5),
      '&:hover': { backgroundColor: '#f0f0f0' },
      '&[aria-selected="true"]': { backgroundColor: '#e0e0e0' },
    },
  },
}));

interface AttributeAutocompleteProps {
  attributes: IAttribute[];
  selected: IAttribute | null;
  inputValue: string;
  error: string | null;
  onChange: (value: AttributeSelectionInput) => Promise<void> | void;
  onInputChange: (value: string) => void;
  disabledAttributeIds?: number[];
  placeholder?: string;
  width?: string;
}

const AttributeAutocomplete: React.FC<AttributeAutocompleteProps> = ({
  attributes,
  selected,
  inputValue,
  error,
  onChange,
  onInputChange,
  disabledAttributeIds = [],
  placeholder,
  width
}) => {
  const classes = useStyles();
  const { formatMessage, locale } = useIntl();
  const { isAuthenticated } = useLoginContext();
  const { unitType } = useUnitContext();
  const [isProcessingSelection, setIsProcessingSelection] = useState(false);
  const disabledAttributeIdSet = useMemo(() => new Set(disabledAttributeIds), [disabledAttributeIds]);
  const compareLocalizedLabels = useMemo(() => createLocalizedLabelComparator(locale), [locale]);
  const sortedAttributes = useMemo(
    () =>
      [...attributes].sort((attributeA, attributeB) => {
        const labelA = getLocalizedAttributeLabel(attributeA, locale);
        const labelB = getLocalizedAttributeLabel(attributeB, locale);
        const labelComparison = compareLocalizedLabels(labelA, labelB);

        return labelComparison || attributeA.id - attributeB.id;
      }),
    [attributes, compareLocalizedLabels, locale],
  );

  // Helper to generate the string "(mm, cm, m)"
  // FIX: Using \u00A0 (Non-Breaking Space) ensures the browser renders the gap.
  const getUnitSuffix = useCallback((attr: IAttribute): string => {
    if (!attr.unitFamily || attr.unitFamily === EAttributeUnitFamily.NONE) {
      return '';
    }

    const allUnits = unitFamilyToUnitRecord[attr.unitFamily] || [];
    let relevantUnits: EAttributeUnit[] = [];

    if (unitType === EUnitType.METRIC) {
        relevantUnits = allUnits.filter((u) => Object.prototype.hasOwnProperty.call(METRIC_TO_IMPERIAL, u));
    } else {
        relevantUnits = allUnits.filter((u) => Object.prototype.hasOwnProperty.call(IMPERIAL_TO_METRIC, u));
    }

    if (relevantUnits.length === 0) return '';

    const labels = relevantUnits.map((u) => attributeUnitDisplayMap[u] || u);
    
    // Using \u00A0 creates a forced space that won't collapse
    return `\u00A0(${labels.join(', ')})`;
  }, [unitType]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: AttributeSelectionInput) => {
      setIsProcessingSelection(true);

      try {
        const result = onChange(value);

        if (result && typeof result.then === 'function') {
          result.then(
            () => setIsProcessingSelection(false),
            () => setIsProcessingSelection(false),
          );
          return;
        }
      } catch (error) {
        setIsProcessingSelection(false);
        throw error;
      }

      setIsProcessingSelection(false);
    },
    [onChange],
  );

  if (!isAuthenticated) return null;

  return (
    <>
      {error ? (
        <Typography style={{ color: '#464646' }}>{error}</Typography>
      ) : (
        <Autocomplete<IAttribute, false, false, true>
          freeSolo
          clearIcon={null}
          disabled={isProcessingSelection}
          forcePopupIcon={false}
          loading={isProcessingSelection}
          options={sortedAttributes}
          getOptionDisabled={(option) => disabledAttributeIdSet.has(option.id)}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          // Only show the Name in the selected Input Field
          getOptionLabel={(o) => (typeof o === 'string' ? o : getLocalizedAttributeLabel(o, locale))}
          
          fullWidth
          value={selected}
          inputValue={inputValue}
          onChange={handleChange}
          onInputChange={(_, val) => onInputChange(val)}
          noOptionsText=""
          renderInput={(params) => (
            <TextField 
              {...params} 
              label={placeholder || formatMessage({ id: 'AttachmentModal.title', defaultMessage: 'Attribut hinzufügen' })} 
              variant="outlined" 
              fullWidth 
              InputProps={{
                ...params.InputProps,
                endAdornment: isProcessingSelection ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment,
              }}
            />
          )}
          renderOption={(props, option) => {
            const label = getLocalizedAttributeLabel(option, locale);
            const suffix = getUnitSuffix(option);
            const isDisabled = disabledAttributeIdSet.has(option.id);
            const optionColor = isDisabled ? '#9a9a9a' : '#464646';
            
            // fullText contains the Non-Breaking Space
            const fullText = label + suffix;
            
            const li = fullText.toLowerCase();
            const iv = inputValue.toLowerCase();
            const idx = li.indexOf(iv);
            
            // Standard View (No Search Match)
            if (idx < 0 || !inputValue) {
              return (
                <li {...props} key={option.id} style={{ color: optionColor }}>
                  {label}
                  {/* Since suffix has \u00A0, it provides its own padding */}
                  {suffix && <span style={{ color: '#888' }}>{suffix}</span>}
                </li>
              );
            }
            
            // Highlight View
            // Slicing works correctly with \u00A0 as it counts as 1 character
            const before = fullText.slice(0, idx);
            const match = fullText.slice(idx, idx + inputValue.length);
            const after = fullText.slice(idx + inputValue.length);
            
            return (
              <li {...props} key={option.id} style={{ color: optionColor }}>
                {before}
                <span style={{ color: isDisabled ? '#9a9a9a' : '#009fe3' }}>{match}</span>
                {after}
              </li>
            );
          }}
          PopperComponent={(props) => (
            <Popper 
              {...props} 
              className={classes.popper} 
              style={{ width: '100%', ...props.style }} 
              modifiers={[{ name: 'computeStyles', options: { adaptive: false } }]} 
            />
          )}
          PaperComponent={(p) => <Paper {...p} className={classes.paper} />}
          ListboxProps={{ className: classes.listbox }}
          className={classes.autocomplete}
          style={{ width }} 
        />
      )}
    </>
  );
};

export default AttributeAutocomplete;
